<template>
  <div class="bg-white rounded">
    <div :class="avatarContainerClasses">
      <member-avatar :is-navbar="props.isNavbar" />
      <member-user-name :is-navbar="props.isNavbar" />
      <div v-if="isNavbar" class="close-btn flex-1 hidden md:block">
        <etg-img src="@/assets/icons/cancel.svg" class="w-10 h-10 p-2 ml-auto -mr-2 cursor-pointer" @click.stop="closeMenu" />
      </div>
    </div>
    <hr :class="isNavbar ? 'mb-2' : 'my-4'" />
    <div :class="routeListClasses">
      <client-only>
        <ul>
          <li v-for="memberRoute in memberRouteList" :key="memberRoute.title" class="cursor-pointer" @click="memberRoute.onClick">
            <component
              :is="memberRoute.component"
              v-if="memberRoute.show"
              class="flex justify-between gap-2 px-5"
              :class="isNavbar ? 'py-3' : 'py-4'"
              :to="memberRoute.to"
            >
              <template v-slot="vSlots">
                <div class="flex items-center w-full">
                  <svg-icon :name="memberRoute.icon" :alt="memberRoute.title" class="mr-2 w-6 h-6" />
                  <span class="text-base">{{ memberRoute.title }}</span>
                  <span v-if="memberRoute.hasDataHint && !vSlots?.isActive" class="text-red-60 ml-2 text-sm bg-red-00 px-2 rounded-0.5 ml-auto">
                    {{ $t('member.profile.not_complete_warning') }}
                  </span>
                </div>
                <div v-if="isNavbar" class="decoration-btn text-base">
                  <svg-icon name="arrow-right" class="w-6 h-6" />
                </div>
              </template>
            </component>
          </li>
          <div v-if="!appStore.isExtraLargeDesktop && props.isNavbar" class="px-5 py-3"><download-app-button class="w-full" /></div>
          <div v-if="props.isNavbar" class="px-1 py-3 border-t-1">
            <etg-button v-if="isLogin" v-gtm:click="GtmEvent.ACCOUNT_LOGGED_OUT" class="w-full justify-left" :center="false" @click="clickLogout">
              {{ $t('member.settings.logout') }}
            </etg-button>
            <login-button v-else class="w-full justify-left bg-transparent" :center="false" @login-btn-click="closeMenu" />
          </div>
        </ul>
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useFeatureToggleStore } from '@/store/feature-toggle'

import { GtmEvent } from '@/types/gtm'

import DownloadAppButton from '@/components/common/download-app-button.vue'
import MemberAvatar from '@/components/member/member-avatar.vue'
import MemberUserName from '@/components/member/member-user-name.vue'
import LocaleLink from '@/components/global/locale-link.vue'
import LoginButton from '@/components/login/login-button.vue'

interface Props {
  isNavbar?: boolean
}

interface Emits {
  (event: 'close-menu'): void
  (event: 'link-clicked'): void
}

const { t } = useI18n()

const route = useRoute()

const appStore = useAppStore()
const authStore = useAuthStore()
const featureToggleStore = useFeatureToggleStore()

const { isLogin } = storeToRefs(authStore)

const props = withDefaults(defineProps<Props>(), {
  isNavbar: false,
})

const emit = defineEmits<Emits>()
const closeMenu = () => emit('close-menu')
const linkClicked = () => emit('link-clicked')

const { selectedLangOption } = useLangSelector()

const memberRouteList = computed(() => [
  {
    to: '/member/orders',
    icon: 'order' as const,
    title: t('booking_list.title'),
    show: isLogin.value,
    component: LocaleLink,
    onClick: linkClicked,
  },
  {
    to: '/member/coupons',
    icon: 'coupon' as const,
    title: t('promo.coupon.title'),
    show: isLogin.value,
    component: LocaleLink,
    onClick: linkClicked,
  },
  {
    to: '/member/favorites',
    icon: 'heart-line' as const,
    title: t('favorite.title'),
    show: featureToggleStore.isFavoriteEnabled && isLogin.value,
    component: LocaleLink,
    onClick: linkClicked,
  },
  {
    to: '/member/personal-info',
    icon: 'person' as const,
    title: t('member.profile'),
    show: isLogin.value,
    component: LocaleLink,
    onClick: linkClicked,
    hasDataHint: !authStore.user?.is_profile_complete,
  },
  {
    to: '/member/account-settings',
    icon: 'settings' as const,
    title: t('member.settings.account'),
    show: isLogin.value,
    component: LocaleLink,
    onClick: linkClicked,
  },
  {
    icon: 'language' as const,
    title: selectedLangOption.value?.label || '',
    show: !appStore.isExtraLargeDesktop && props.isNavbar,
    component: 'div',
    onClick: onLangClick,
  },
])

const avatarContainerClasses = computed(() => (props.isNavbar ? 'px-5 py-3 flex items-center gap-3 md:py-4' : 'py-6'))
const routeListClasses = computed(() => (props.isNavbar ? '' : 'pb-4'))

const clickLogout = () => {
  if ((route.meta?.middleware as string | string[])?.includes('auth-required')) {
    authStore.logout(false)
    window.location.replace('/')
  } else {
    authStore.logout()
    closeMenu()
  }
}

const onLangClick = () => {
  if (appStore.isDesktopScreen) {
    closeMenu()
  }
  appStore.isLangSwitcherShown = true
}
</script>
